import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CropCacheService } from '@services/caches/crop-cache.service';
import { ServerTranslateService } from '@services/server-translate.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, interval, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { MarkdownPipe } from '@app/utils/pipes/markdown-pipe';
import { UserProfileService } from '@services/user-profile.service';
import { AnalyticsCatalogService } from './analytics-catalog.service';
import { REPORT_GENERATOR_PATH, parseReportGeneratorQuery } from './report-generator-query.util';
import { AnalyticsSessionStore } from './analytics-session.store';
import { ArchiveTabComponent } from './components/archive-tab.component';
import { AnalyticsMapCardComponent } from './components/analytics-map-card.component';
import { datasetHasGeography, datasetOffersRaster, mapsHaveGeography } from './components/analytics-map.logic';
import { ChartCardComponent } from './components/chart-card.component';
import { ReportsTabComponent } from './components/reports-tab.component';
import { SubscriptionsTabComponent } from './components/subscriptions-tab.component';
import {
  ChatMessageRecord,
  ApplyConstructorTemplateResponse,
  ChatSessionSummary,
  ConstructorTemplateItem,
  RefreshDatasetResponse,
  ReportJobItem,
  isActiveJobStatus
} from './models/analytics-catalog.models';
import {
  AnalyticsChatMessage,
  ChatChartArtifact,
  ChatDatasetArtifact,
  ChatMapArtifact,
  ChatFileArtifact,
  ChatHttpResponse,
  ChatJobArtifact,
  ChatSessionContext,
  ChatTurnArtifacts,
  DataConstraints,
  emptyArtifacts,
  newAnalyticsId
} from './models/orchestrator-chat.models';
import { OrchestratorChatService } from './orchestrator-chat.service';

type ModuleTab = 'chat' | 'reports' | 'subscriptions' | 'archive';

interface AnalyticsMapView {
  maps: ChatMapArtifact[];
  dataset: ChatDatasetArtifact | null;
  /** The latest answer had nothing to draw, so the map shows an earlier answer. */
  stale: boolean;
}

const CONSTRUCTOR_LABELS: Record<string, Record<string, string>> = {
  ru: {
    constructor_title: 'Название',
    constructor_period_from: 'Период с',
    constructor_period_to: 'Период по',
    constructor_add_section: 'Добавить секцию',
    constructor_section_title: 'Заголовок секции',
    constructor_remove_section: 'Убрать секцию',
    constructor_no_dataset: 'В этом чате ещё нет сохранённого датасета. Запросите данные ещё раз.',
    constructor_compose_failed: 'Не удалось поставить файл в очередь.',
    constructor_drag_hint: 'Перетащите заголовок в таблице, чтобы изменить порядок столбцов в отчёте.',
    constructor_pick_one: 'Отметьте хотя бы один столбец',
    constructor_preview_note: 'В превью видны только столбцы ответа. Файл соберётся по всему отмеченному списку.',
    constructor_region: 'Регион',
    constructor_crop: 'Культура',
    constructor_crop_any: 'Без фильтра',
    constructor_refresh: 'Обновить данные',
    constructor_filter_rejected: 'Фильтр не подходит этому датасету',
    constructor_template_name: 'Название шаблона',
    constructor_template: 'Шаблон',
    constructor_save_template: 'Сохранить шаблон',
    constructor_open_template: 'Открыть шаблон',
    constructor_template_save_failed: 'Не удалось сохранить шаблон',
    constructor_template_failed: 'Не удалось открыть шаблон',
    constructor_template_no_session: 'Сначала откройте чат с данными',
    constructor_subscribe: 'Подписаться на этот шаблон',
    constructor_subscribe_time: 'Время',
    constructor_subscribe_pass: 'При новом пролёте спутника',
    constructor_subscribe_days: 'Выберите хотя бы один день',
    constructor_subscribe_geo: 'Для пролёта нужен регион',
    constructor_subscribe_failed: 'Шаблон сохранён, подписку оформить не удалось',
    constructor_subscribed: 'Подписка сохранена. Файл появится во вкладке «Подписки».',
    constructor_dataset: 'Данные',
    constructor_rows: 'строк',
    constructor_pick_dataset: 'Выберите данные…',
    constructor_add: 'Добавить',
    constructor_region_required: 'Укажите регион: без него данные нельзя запросить заново',
    constructor_template_saved: 'Шаблон сохранён',
    constructor_all: 'Все показатели',
    constructor_all_hint: 'Отметить все показатели без служебных идентификаторов',
    constructor_ids: 'Идентификаторы',
    constructor_ids_remove: 'Убрать идентификаторы',
    constructor_ids_hint: 'Добавить в таблицу служебные идентификаторы (ID культуры, района, поля) для сверки с другими данными'
  },
  en: {
    constructor_title: 'Title',
    constructor_period_from: 'Period from',
    constructor_period_to: 'Period to',
    constructor_add_section: 'Add section',
    constructor_section_title: 'Section title',
    constructor_remove_section: 'Remove section',
    constructor_no_dataset: 'This chat has no stored dataset yet. Ask for the data again.',
    constructor_compose_failed: 'Could not queue the file.',
    constructor_drag_hint: 'Drag a table header to change the column order in the report.',
    constructor_pick_one: 'Select at least one column',
    constructor_preview_note: 'The preview only shows columns from the answer. The file uses the full selection.',
    constructor_region: 'Region',
    constructor_crop: 'Crop',
    constructor_crop_any: 'No filter',
    constructor_refresh: 'Refresh data',
    constructor_filter_rejected: 'This filter does not fit this dataset',
    constructor_template_name: 'Template name',
    constructor_template: 'Template',
    constructor_save_template: 'Save template',
    constructor_open_template: 'Open template',
    constructor_template_save_failed: 'Could not save the template',
    constructor_template_failed: 'Could not open the template',
    constructor_template_no_session: 'Open a chat that already has the data',
    constructor_subscribe: 'Subscribe to this template',
    constructor_subscribe_time: 'Time',
    constructor_subscribe_pass: 'When a new satellite pass arrives',
    constructor_subscribe_days: 'Select at least one day',
    constructor_subscribe_geo: 'A region is required for a satellite pass',
    constructor_subscribe_failed: 'The template was saved, but the subscription was not',
    constructor_subscribed: 'Subscription saved. The file will appear on the Subscriptions tab.',
    constructor_dataset: 'Data',
    constructor_rows: 'rows',
    constructor_pick_dataset: 'Choose data…',
    constructor_add: 'Add',
    constructor_region_required: 'Select a region: the data cannot be queried again without it',
    constructor_template_saved: 'Template saved',
    constructor_all: 'All indicators',
    constructor_all_hint: 'Select every indicator without technical ids',
    constructor_ids: 'Identifiers',
    constructor_ids_remove: 'Remove identifiers',
    constructor_ids_hint: 'Add technical ids (crop, district, field) to the table to match it with other data'
  },
  uz: {
    constructor_title: 'Nomi',
    constructor_period_from: 'Davr dan',
    constructor_period_to: 'Davr gacha',
    constructor_add_section: "Bo'lim qo'shish",
    constructor_section_title: 'Bo\'lim sarlavhasi',
    constructor_remove_section: "Bo'limni olib tashlash",
    constructor_no_dataset: "Bu chatda saqlangan dataset yo'q. Ma'lumotni qayta so'rang.",
    constructor_compose_failed: "Faylni navbatga qo'yib bo'lmadi.",
    constructor_drag_hint: "Hisobotdagi ustunlar tartibini o'zgartirish uchun jadval sarlavhasini sudrab o'tkazing.",
    constructor_pick_one: 'Kamida bitta ustunni belgilang',
    constructor_preview_note: "Ko'rinishda faqat javob ustunlari bor. Fayl belgilangan ro'yxat bo'yicha yig'iladi.",
    constructor_region: 'Hudud',
    constructor_crop: 'Ekin',
    constructor_crop_any: 'Filtrsiz',
    constructor_refresh: "Ma'lumotni yangilash",
    constructor_filter_rejected: "Bu filtr ushbu datasetga mos kelmaydi",
    constructor_template_name: 'Shablon nomi',
    constructor_template: 'Shablon',
    constructor_save_template: 'Shablonni saqlash',
    constructor_open_template: 'Shablonni ochish',
    constructor_template_save_failed: "Shablonni saqlab bo'lmadi",
    constructor_template_failed: "Shablonni ochib bo'lmadi",
    constructor_template_no_session: "Avval ma'lumotli chatni oching",
    constructor_subscribe: 'Bu shablonga obuna bo\'lish',
    constructor_subscribe_time: 'Vaqt',
    constructor_subscribe_pass: 'Yangi sun\'iy yo\'ldosh parvozi kelganda',
    constructor_subscribe_days: 'Kamida bitta kunni tanlang',
    constructor_subscribe_geo: 'Parvoz uchun hudud kerak',
    constructor_subscribe_failed: 'Shablon saqlandi, obuna rasmiylashtirilmadi',
    constructor_subscribed: 'Obuna saqlandi. Fayl «Obunalar» yorlig\'ida paydo bo\'ladi.',
    constructor_dataset: "Ma'lumotlar",
    constructor_rows: 'qator',
    constructor_pick_dataset: "Ma'lumotni tanlang…",
    constructor_add: "Qo'shish",
    constructor_region_required: "Hududni ko'rsating: usiz ma'lumotni qayta so'rab bo'lmaydi",
    constructor_template_saved: 'Shablon saqlandi',
    constructor_all: "Barcha ko'rsatkichlar",
    constructor_all_hint: "Xizmat identifikatorlarisiz barcha ko'rsatkichlarni belgilash",
    constructor_ids: 'Identifikatorlar',
    constructor_ids_remove: 'Identifikatorlarni olib tashlash',
    constructor_ids_hint: "Boshqa ma'lumotlar bilan solishtirish uchun jadvalga xizmat identifikatorlarini (ekin, tuman, maydon ID) qo'shish"
  }
};

interface FilterOption {
  id: string;
  label: string;
}

interface ReportColumnOption {
  key: string;
  label_ru?: string;
  label_uz?: string;
  label_en?: string;
  default_selected?: boolean;
  /** Raw id column: offered on request, never selected by default. */
  identifier?: boolean;
}

/** Keys a "select everything" action picks: every indicator, but not the raw ids. */
function indicatorKeys(catalog: ReportColumnOption[]): string[] {
  return catalog.filter((column) => !isIdentifierColumn(column)).map((column) => column.key);
}

/** Ids stay out of the default table even when an older catalog did not flag them. */
function isIdentifierColumn(column: ReportColumnOption): boolean {
  return column.identifier === true || /_id$/i.test(column.key);
}

/** Default report columns, falling back to every indicator when the catalog has no defaults. */
function defaultKeys(catalog: ReportColumnOption[]): string[] {
  const defaults = catalog
    .filter((column) => !isIdentifierColumn(column) && column.default_selected !== false)
    .map((column) => column.key);
  return defaults.length ? defaults : indicatorKeys(catalog);
}

/** Presentation aliases from the report catalog: the server resolves them to the localized name column. */
const COLUMN_ALIASES: Record<string, string> = {
  '@crop_name': 'crop_name',
  '@region_name': 'name',
  '@sowing_name': 'sowing_name'
};

/** Labels for every interface language, used when the stored catalog only has Russian. */
const COLUMN_LABELS: Record<string, readonly [string, string, string]> = {
  '@crop_name': ['Культура', 'Ekin', 'Crop'],
  '@region_name': ['Район', 'Hudud', 'District'],
  field_name: ['Поле', 'Maydon', 'Field'],
  '@sowing_name': ['Посев', 'Ekish', 'Sowing'],
  season_year: ['Год', 'Yil', 'Year'],
  season_cycle: ['Цикл сева', 'Ekin tsikli', 'Sowing cycle'],
  declared_area_ha: ['Заявленная площадь, га', "E'lon qilingan maydon, ga", 'Declared area, ha'],
  recognized_area_ha: ['Распознанная площадь, га', 'Aniqlangan maydon, ga', 'Recognized area, ha'],
  total_area_ha: ['Общая площадь, га', 'Jami maydon, ga', 'Total area, ha'],
  field_count: ['Число полей', 'Maydonlar soni', 'Fields'],
  area_diff_pct: ['Расхождение, %', 'Farq, %', 'Difference, %'],
  anomaly_type: ['Тип аномалии', 'Anomaliya turi', 'Anomaly type'],
  detected_date: ['Дата обнаружения', 'Aniqlangan sana', 'Detected on'],
  end_date: ['Дата окончания', 'Tugash sanasi', 'End date'],
  is_open: ['Открыта', 'Ochiq', 'Open'],
  loss_area_ha: ['Площадь потерь, га', "Yo'qotish maydoni, ga", 'Loss area, ha'],
  forecast_date: ['Дата прогноза', 'Prognoz sanasi', 'Forecast date'],
  area_ha: ['Площадь, га', 'Maydon, ga', 'Area, ha'],
  yield_forecast_t_ha: ['Наиболее вероятная урожайность, т/га', 'Eng ehtimoliy hosildorlik, t/ga', 'Most likely yield, t/ha'],
  pessimistic_yield_t_ha: ['Пессимистичная урожайность, т/га', 'Pessimistik hosildorlik, t/ga', 'Pessimistic yield, t/ha'],
  optimistic_yield_t_ha: ['Оптимистичная урожайность, т/га', 'Optimistik hosildorlik, t/ga', 'Optimistic yield, t/ha'],
  gross_forecast_t: ['Наиболее вероятный валовой сбор, т', 'Eng ehtimoliy yalpi hosil, t', 'Most likely gross harvest, t'],
  pessimistic_harvest_t: ['Пессимистичный валовой сбор, т', 'Pessimistik yalpi hosil, t', 'Pessimistic gross harvest, t'],
  optimistic_harvest_t: ['Оптимистичный валовой сбор, т', 'Optimistik yalpi hosil, t', 'Optimistic gross harvest, t'],
  date: ['Дата', 'Sana', 'Date'],
  index_type: ['Индекс', 'Indeks', 'Index'],
  value_median: ['Медиана', 'Mediana', 'Median'],
  season_count: ['Число сезонов', 'Mavsumlar soni', 'Seasons'],
  sample_count: ['Число измерений', "O'lchovlar soni", 'Samples'],
  period_start: ['Начало периода', 'Davr boshi', 'Period start'],
  period_end: ['Конец периода', 'Davr oxiri', 'Period end'],
  tavg_c: ['Средняя температура, °C', "O'rtacha harorat, °C", 'Mean temperature, °C'],
  t2m_mean_c: ['Средняя температура, °C', "O'rtacha harorat, °C", 'Mean temperature, °C'],
  tmax_c: ['Макс. температура, °C', 'Maks. harorat, °C', 'Max temperature, °C'],
  t2m_max_c: ['Макс. температура, °C', 'Maks. harorat, °C', 'Max temperature, °C'],
  tmin_c: ['Мин. температура, °C', 'Min. harorat, °C', 'Min temperature, °C'],
  t2m_min_c: ['Мин. температура, °C', 'Min. harorat, °C', 'Min temperature, °C'],
  precipitation_mm: ['Осадки, мм', "Yog'ingarchilik, mm", 'Precipitation, mm'],
  precipitation_mm_total: ['Осадки, мм', "Yog'ingarchilik, mm", 'Precipitation, mm'],
  humidity_pct: ['Влажность, %', 'Namlik, %', 'Humidity, %'],
  relative_humidity_pct: ['Влажность, %', 'Namlik, %', 'Humidity, %'],
  wind_speed_mean_ms: ['Ветер, м/с', 'Shamol, m/s', 'Wind, m/s'],
  solar_radiation_mj: ['Солнечная радиация, МДж', 'Quyosh radiatsiyasi, MJ', 'Solar radiation, MJ'],
  solar_radiation_mj_total: ['Солнечная радиация, МДж', 'Quyosh radiatsiyasi, MJ', 'Solar radiation, MJ'],
  solar_radiation_mj_per_day: ['Радиация, МДж/сут', 'Radiatsiya, MJ/kun', 'Radiation, MJ/day'],
  frost_day: ['Морозные дни', 'Sovuq kunlar', 'Frost days'],
  frost_days: ['Морозные дни', 'Sovuq kunlar', 'Frost days'],
  heat_day_35c: ['Жаркие дни', 'Issiq kunlar', 'Hot days'],
  heat_days_35c: ['Жаркие дни', 'Issiq kunlar', 'Hot days'],
  station_samples: ['Число станций', 'Stansiyalar', 'Stations'],
  norm_tavg_c: ['Норма средней температуры, °C', "O'rtacha harorat me'yori, °C", 'Normal mean temperature, °C'],
  norm_tmin_c: ['Норма мин. температуры, °C', "Min. harorat me'yori, °C", 'Normal min temperature, °C'],
  norm_tmax_c: ['Норма макс. температуры, °C', "Maks. harorat me'yori, °C", 'Normal max temperature, °C'],
  norm_precipitation_mm: ['Норма осадков, мм', "Yog'ingarchilik me'yori, mm", 'Normal precipitation, mm'],
  norm_humidity_pct: ['Норма влажности, %', "Namlik me'yori, %", 'Normal humidity, %'],
  month: ['Месяц', 'Oy', 'Month'],
  day: ['День', 'Kun', 'Day'],
  risk_index: ['Индекс риска', 'Xavf indeksi', 'Risk index'],
  severity: ['Серьёзность', 'Jiddiylik', 'Severity'],
  monetary_loss: ['Денежные потери', "Pul yo'qotishlari", 'Monetary loss'],
  crop_id: ['Идентификатор культуры', 'Ekin identifikatori', 'Crop id'],
  region_id: ['Идентификатор района', 'Hudud identifikatori', 'District id'],
  geo_id: ['Идентификатор района', 'Hudud identifikatori', 'District id'],
  field_id: ['Идентификатор поля', 'Maydon identifikatori', 'Field id'],
  season_id: ['Идентификатор сезона', 'Mavsum identifikatori', 'Season id'],
  actual_polygon_id: ['Идентификатор полигона', 'Poligon identifikatori', 'Polygon id'],
  parent_id: ['Идентификатор родителя', 'Ota identifikatori', 'Parent id'],
  anomaly_id: ['Идентификатор аномалии', 'Anomaliya identifikatori', 'Anomaly id'],
  analysis_id: ['Идентификатор анализа', 'Tahlil identifikatori', 'Analysis id'],
  fly_path_id: ['Идентификатор маршрута', 'Marshrut identifikatori', 'Flight path id'],
  producer_id: ['Идентификатор производителя', 'Ishlab chiqaruvchi identifikatori', 'Producer id']
};

/** At most two digits after the decimal point. Whole numbers stay whole. */
function formatTableNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const text = value.toFixed(2);
  return text.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function knownColumnLabel(key: string, locale: string): string | null {
  const labels = COLUMN_LABELS[key];
  if (!labels) {
    return null;
  }
  return locale === 'uz' ? labels[1] : locale === 'en' ? labels[2] : labels[0];
}

interface ConstructorSection {
  datasetId: string;
  tool: string;
  title: string;
  catalog: ReportColumnOption[];
  selectedKeys: string[];
  previewRows: Record<string, unknown>[];
  previewColumns: string[];
  /** Rows were produced by opening a saved template, so the dataset is not among the chat messages. */
  fromTemplate?: boolean;
}

/** Datasets stored under this name arrived without the MCP tool that produced them. */
const UNKNOWN_TOOL = 'assistant';
const WEATHER_TOOLS = new Set(['get_weather_metrics', 'get_weather_forecast', 'get_climate_norm']);

@Component({
  selector: 'app-reporting-analytics-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MarkdownPipe,
    ChartCardComponent,
    AnalyticsMapCardComponent,
    ReportsTabComponent,
    SubscriptionsTabComponent,
    ArchiveTabComponent
  ],
  templateUrl: './reporting-analytics-page.component.html',
  styleUrl: './reporting-analytics-page.component.scss'
})
export class ReportingAnalyticsPageComponent implements OnInit, OnDestroy {
  @ViewChild('feed') feed?: ElementRef<HTMLDivElement>;
  @ViewChild('mapCard') mapCard?: AnalyticsMapCardComponent;

  messages: AnalyticsChatMessage[] = [];
  draft = '';
  sending = false;
  loadingHistory = false;
  llmUnavailable = false;
  search = '';
  sessions: ChatSessionSummary[] = [];
  sessionsLoading = false;
  sessionsError = false;
  sessionId: string | null = null;
  sessionContext: ChatSessionContext | null = null;
  constraints: DataConstraints | null = null;
  auditOpen = new Set<string>();
  activeModuleTab: ModuleTab = 'chat';
  activeResultTab: 'data' | 'map' | 'chart' = 'data';
  constructorMode = false;
  columnError = false;
  composeError = '';
  composeNotice = '';
  documentTitle = '';
  periodFrom = '';
  periodTo = '';
  filterGeoId = '';
  filterCropId = '';
  templateName = '';
  selectedTemplateId = '';
  pendingDatasetId = '';
  /** Section headings typed by the user, keyed by dataset id, so a removed and re-added section keeps its name. */
  private readonly sectionTitles = new Map<string, string>();
  /** Dataset ids whose section title was typed by the user, so a language switch does not replace it. */
  private readonly titleEdited = new Set<string>();
  private documentTitleEdited = false;
  subscribeTemplate = false;
  subscribeDays = [1];
  subscribeTime = '09:00';
  subscribeFormat = 'pdf';
  subscribeOnNewPass = false;
  savedTemplates: ConstructorTemplateItem[] = [];
  savingTemplate = false;
  openingTemplate = false;
  regionOptions: FilterOption[] = [];
  cropOptions: FilterOption[] = [];
  refreshing = false;
  sections: ConstructorSection[] = [];
  extraJobs: ChatJobArtifact[] = [];
  selectedColumnKeys: string[] = [];
  draggingColumn: string | null = null;
  draggingSectionId: string | null = null;
  dragOverColumn: string | null = null;
  private columnDatasetId: string | null = null;
  private filterCatalogsLoaded = false;
  /** Crop that came from `/reports/generator`; it stays selected even when the monitoring-crop catalog does not list it. */
  private deepLinkCropId: string | null = null;
  renamingSessionId: string | null = null;
  renameDraft = '';
  quickPrompts = [
    'reporting_analytics.prompt_weather',
    'reporting_analytics.prompt_areas',
    'reporting_analytics.prompt_ndvi',
    'reporting_analytics.prompt_yield',
    'reporting_analytics.prompt_calendar'
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private readonly imagePreviewUrls = new Map<string, string>();
  private pollSub?: Subscription;

  constructor(
    private readonly chatApi: OrchestratorChatService,
    private readonly catalog: AnalyticsCatalogService,
    private readonly sessionsStore: AnalyticsSessionStore,
    private readonly profile: UserProfileService,
    private readonly translate: TranslateService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly route: ActivatedRoute,
    private readonly cropCache: CropCacheService,
    private readonly serverTranslate: ServerTranslateService
  ) {}

  ngOnInit(): void {
    this.refreshSessions();
    this.search$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.refreshSessions());

    const current = this.sessionsStore.currentId(this.userId);
    if (current) {
      this.loadSession(current);
    }

    this.chatApi.getDataConstraints().pipe(takeUntil(this.destroy$)).subscribe({
      next: (c) => (this.constraints = c),
      error: () => (this.constraints = null)
    });

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.retitleForLocale());

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyReportGeneratorQuery());
  }

  /**
   * `/reports/generator?region_id&district_id&crop_id&year`: opens the constructor and fills its
   * region, crop and period filters. Nothing else (tabs, chat session) is reset.
   */
  private applyReportGeneratorQuery(): void {
    if (this.route.snapshot.routeConfig?.path !== REPORT_GENERATOR_PATH) {
      return;
    }
    const query = parseReportGeneratorQuery(this.route.snapshot.queryParamMap);
    if (!query) {
      return;
    }
    this.activeModuleTab = 'chat';
    // Filters first: the catalog loaders started by the constructor mode read them (a link crop must survive).
    this.filterGeoId = query.geoId ?? '';
    this.filterCropId = query.cropId ?? '';
    this.deepLinkCropId = query.cropId;
    this.periodFrom = query.periodFrom;
    this.periodTo = query.periodTo;
    this.setConstructorMode(true);
    if (query.geoId) {
      this.ensureRegionOption(query.geoId);
    }
  }

  ngOnDestroy(): void {
    this.clearImagePreviews();
    this.destroy$.next();
    this.destroy$.complete();
    this.pollSub?.unsubscribe();
  }

  get userId(): string {
    return this.profile.getUserId() || 'anon';
  }

  get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'ru';
    return lang.startsWith('uz') ? 'uz' : lang.startsWith('en') ? 'en' : 'ru';
  }

  constructorText(key: string): string {
    const lang = this.translate.currentLang || 'ru-RU';
    const bundle = this.translate.translations?.[lang] as { reporting_analytics?: Record<string, string> } | undefined;
    const fromFile = bundle?.reporting_analytics?.[key];
    if (fromFile) {
      return fromFile;
    }
    return CONSTRUCTOR_LABELS[this.locale][key] || this.translate.instant(`reporting_analytics.${key}`);
  }

  get composerPlaceholderKey(): string {
    return this.constructorMode
      ? 'reporting_analytics.constructor_placeholder'
      : 'reporting_analytics.composer_placeholder';
  }

  get columnCatalog(): ReportColumnOption[] {
    this.ensureColumnSelection();
    return this.readColumnCatalog();
  }

  get visibleColumns(): string[] {
    const preview = this.latestDataset?.columns ?? [];
    if (!this.constructorMode) {
      return preview;
    }
    this.ensureColumnSelection();
    const available = new Set(preview);
    return this.selectedColumnKeys.filter((key) => this.resolvePreviewKey(key, available) !== null);
  }

  get canReorderColumns(): boolean {
    return this.constructorMode && this.visibleColumns.length > 1;
  }

  get previewMissingSelection(): boolean {
    if (!this.constructorMode || !this.latestDataset) {
      return false;
    }
    const preview = new Set(this.latestDataset.columns);
    return this.selectedColumnKeys.some((key) => !preview.has(key));
  }

  get starredSessions(): ChatSessionSummary[] {
    return this.sessions.filter((s) => s.starred);
  }

  get recentSessions(): ChatSessionSummary[] {
    return this.sessions.filter((s) => !s.starred);
  }

  get latestDataset(): ChatDatasetArtifact | null {
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      const datasets = this.messages[i].artifacts?.datasets;
      if (datasets?.length) {
        return datasets[datasets.length - 1];
      }
    }
    return null;
  }

  private cachedMapView: { key: string; value: AnalyticsMapView } | null = null;

  get activeMap(): AnalyticsMapView {
    const key = this.messages
      .map((message) => {
        const datasets = message.artifacts?.datasets ?? [];
        const dataset = datasets[datasets.length - 1];
        return `${message.id}:${message.text?.length ?? 0}:${message.artifacts?.maps?.length ?? 0}:${dataset?.preview_rows?.length ?? 0}`;
      })
      .join('|');
    if (this.cachedMapView?.key === key) {
      return this.cachedMapView.value;
    }
    const value = this.resolveActiveMap();
    this.cachedMapView = { key, value };
    return value;
  }

  get hasMap(): boolean {
    const active = this.activeMap;
    return this.viewHasMap(active.maps, active.dataset);
  }

  private viewHasMap(maps: ChatMapArtifact[], dataset: ChatDatasetArtifact | null): boolean {
    return mapsHaveGeography(maps) || datasetHasGeography(dataset) || datasetOffersRaster(dataset);
  }

  /** Contours or points the assistant returned for this turn, as opposed to a raster the card could offer. */
  private static turnHasGeography(artifacts: ChatTurnArtifacts | undefined): boolean {
    if (!artifacts) {
      return false;
    }
    const datasets = artifacts.datasets ?? [];
    const dataset = datasets.length > 0 ? datasets[datasets.length - 1] : null;
    return mapsHaveGeography(artifacts.maps) || datasetHasGeography(dataset);
  }

  /**
   * The newest assistant answer that has something to draw. When the latest answer has no
   * geography the previous map is kept, but flagged as stale so the user sees it is not from
   * the answer they are reading.
   */
  private resolveActiveMap(): AnalyticsMapView {
    let latestSeen = false;
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      const message = this.messages[i];
      if (message.role !== 'assistant') {
        continue;
      }
      const maps = message.artifacts?.maps ?? [];
      const datasets = message.artifacts?.datasets ?? [];
      const dataset = datasets.length > 0 ? datasets[datasets.length - 1] : null;
      const isLatest = !latestSeen;
      latestSeen = true;
      if (this.viewHasMap(maps, dataset)) {
        return { maps, dataset, stale: !isLatest };
      }
    }
    return { maps: [], dataset: null, stale: false };
  }

  get latestChart(): ChatChartArtifact | null {
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      const charts = this.messages[i].artifacts?.charts;
      if (charts?.length) {
        return charts[charts.length - 1];
      }
    }
    return null;
  }

  get allJobs(): ChatJobArtifact[] {
    return [...this.messages.flatMap((message) => message.artifacts?.jobs ?? []), ...this.extraJobs];
  }

  get addableDatasets(): ChatDatasetArtifact[] {
    const used = new Set(this.sections.map((section) => section.datasetId));
    return this.sessionDatasets().filter((dataset) => dataset.dataset_id && !used.has(dataset.dataset_id));
  }

  get hasConversation(): boolean {
    return this.messages.length > 0 || this.loadingHistory;
  }

  get datasetTotal(): number {
    const value = this.latestDataset?.meta?.['row_count'];
    return typeof value === 'number' ? value : this.latestDataset?.preview_rows.length ?? 0;
  }

  setModuleTab(tab: ModuleTab): void {
    this.activeModuleTab = tab;
  }

  setResultTab(tab: 'data' | 'map' | 'chart'): void {
    this.activeResultTab = tab;
    if (tab === 'map') {
      setTimeout(() => this.mapCard?.invalidate(), 0);
    }
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.search$.next(value);
  }

  sessionTitle(session: ChatSessionSummary): string {
    return session.title?.trim() || this.translate.instant('reporting_analytics.untitled_session');
  }

  datasetCell(row: Record<string, unknown>, column: string): string {
    const key = this.resolvePreviewKey(column, new Set(Object.keys(row))) ?? column;
    const value = row[key];
    if (value == null) {
      return '—';
    }
    return typeof value === 'number' ? formatTableNumber(value) : String(value);
  }

  jobLabel(job: ChatJobArtifact): string {
    return `${(job.format || 'report').toUpperCase()} #${job.job_id.slice(0, 4)}`;
  }

  jobProgress(job: ChatJobArtifact): number {
    if (job.status === 'done') {
      return 100;
    }
    return Math.max(0, Math.min(100, job.progress_pct ?? 0));
  }

  newSession(): void {
    this.clearImagePreviews();
    this.sessionId = null;
    this.messages = [];
    this.llmUnavailable = false;
    this.columnError = false;
    this.composeError = '';
    this.composeNotice = '';
    this.documentTitle = '';
    this.periodFrom = '';
    this.periodTo = '';
    this.filterGeoId = '';
    this.filterCropId = '';
    this.templateName = '';
    this.selectedTemplateId = '';
    this.pendingDatasetId = '';
    this.sectionTitles.clear();
    this.titleEdited.clear();
    this.documentTitleEdited = false;
    this.sections = [];
    this.extraJobs = [];
    this.selectedColumnKeys = [];
    this.columnDatasetId = null;
    this.pollSub?.unsubscribe();
    this.sessionsStore.clearCurrent(this.userId);
  }

  setConstructorMode(enabled: boolean): void {
    if (this.constructorMode === enabled) {
      return;
    }
    this.constructorMode = enabled;
    this.columnError = false;
    if (enabled) {
      this.columnDatasetId = null;
      this.ensureColumnSelection();
      this.syncSections();
      this.loadFilterCatalogs();
      this.loadTemplates();
    }
  }

  get subscribeWeekdays(): Array<{ id: number; label: string }> {
    const labels = this.locale === 'en'
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : this.locale === 'uz'
        ? ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya']
        : ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    return labels.map((label, index) => ({ id: index === 6 ? 0 : index + 1, label }));
  }

  toggleSubscribeDay(day: number): void {
    this.subscribeDays = this.subscribeDays.includes(day)
      ? this.subscribeDays.filter((item) => item !== day)
      : [...this.subscribeDays, day];
  }

  saveTemplate(): void {
    const title = this.templateName.trim() || this.documentTitle.trim();
    if (!title || !this.sections.length || this.savingTemplate) {
      return;
    }
    this.composeError = '';
    this.composeNotice = '';
    if (this.sections.some((section) => !section.selectedKeys.length)) {
      this.columnError = true;
      return;
    }
    this.columnError = false;
    // The server recognizes unnamed datasets by their rows, so only block when the region is clearly missing.
    if (!this.filterGeoId && this.sections.some((section) => !WEATHER_TOOLS.has(section.tool))) {
      this.composeError = this.constructorText('constructor_region_required');
      return;
    }
    if (this.subscribeTemplate && !this.subscribeOnNewPass && !this.subscribeDays.length) {
      this.composeError = this.constructorText('constructor_subscribe_days');
      return;
    }
    if (this.subscribeTemplate && this.subscribeOnNewPass && !this.filterGeoId) {
      this.composeError = this.constructorText('constructor_subscribe_geo');
      return;
    }
    const selected = this.savedTemplates.find((item) => item.template_id === this.selectedTemplateId);
    this.savingTemplate = true;
    this.catalog
      .saveConstructorTemplate({
        template_id: selected && selected.title === title ? selected.template_id : null,
        title,
        period_from: this.periodFrom || null,
        period_to: this.periodTo || null,
        geo_id: this.filterGeoId,
        crop_id: this.filterCropId,
        sections: this.sections.map((section) => ({
          dataset_id: section.datasetId,
          title: section.title,
          columns: section.selectedKeys
        }))
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (saved) => {
          this.templateName = saved.title;
          this.selectedTemplateId = saved.template_id;
          this.loadTemplates();
          if (!this.subscribeTemplate) {
            this.savingTemplate = false;
            this.composeNotice = this.constructorText('constructor_template_saved');
            return;
          }
          const scheduled = this.subscribeDays.length > 0;
          this.catalog
            .subscribeConstructorTemplate(saved.template_id, {
              format: this.subscribeFormat,
              days: scheduled ? this.subscribeDays : undefined,
              time: scheduled ? this.subscribeTime : undefined,
              trigger_on_new_pass: this.subscribeOnNewPass
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.savingTemplate = false;
                this.composeNotice = this.constructorText('constructor_subscribed');
              },
              error: (err: HttpErrorResponse) => {
                this.savingTemplate = false;
                this.composeError = this.apiError(err, 'constructor_subscribe_failed');
              }
            });
        },
        error: (err: HttpErrorResponse) => {
          this.savingTemplate = false;
          this.composeError = this.apiError(err, 'constructor_template_save_failed');
        }
      });
  }

  openTemplate(): void {
    if (!this.selectedTemplateId || this.openingTemplate) {
      return;
    }
    this.openingTemplate = true;
    this.composeError = '';
    this.composeNotice = '';
    this.catalog
      .applyConstructorTemplate(this.selectedTemplateId, this.sessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.openingTemplate = false;
          this.sessionId = result.session_id;
          this.sessionsStore.setCurrent(this.userId, result.session_id);
          this.applyTemplate(result);
          this.refreshSessions();
        },
        error: (err: HttpErrorResponse) => {
          this.openingTemplate = false;
          this.composeError = this.apiError(err, 'constructor_template_failed');
        }
      });
  }

  onTemplatePicked(templateId: string): void {
    const selected = this.savedTemplates.find((item) => item.template_id === templateId);
    if (selected) {
      this.templateName = selected.title;
    }
  }

  /** Label for a chat dataset in the "add section" picker: the tool name, or a caption when the tool is unknown. */
  datasetLabel(dataset: ChatDatasetArtifact): string {
    const remembered = dataset.dataset_id && this.titleEdited.has(dataset.dataset_id)
      ? this.sectionTitles.get(dataset.dataset_id)
      : undefined;
    if (remembered) {
      return remembered;
    }
    return this.autoDatasetLabel(dataset);
  }

  /** Caption generated from the dataset, in the current interface language. */
  private autoDatasetLabel(dataset: ChatDatasetArtifact): string {
    if (dataset.tool && dataset.tool !== UNKNOWN_TOOL) {
      return dataset.tool;
    }
    const parts = [this.constructorText('constructor_dataset')];
    const period = dataset.meta?.['period'];
    if (period && typeof period === 'object') {
      const value = period as { from?: unknown; to?: unknown };
      if (typeof value.from === 'string' && typeof value.to === 'string') {
        parts.push(`${value.from.slice(0, 10)} — ${value.to.slice(0, 10)}`);
      }
    }
    const rowCount = dataset.meta?.['row_count'];
    if (typeof rowCount === 'number') {
      parts.push(`${rowCount} ${this.constructorText('constructor_rows')}`);
    }
    return parts.join(' · ');
  }

  rememberSectionTitle(section: ConstructorSection): void {
    if (section.title.trim()) {
      this.sectionTitles.set(section.datasetId, section.title.trim());
    } else {
      this.sectionTitles.delete(section.datasetId);
    }
  }

  markSectionTitleEdited(section: ConstructorSection): void {
    this.titleEdited.add(section.datasetId);
    this.rememberSectionTitle(section);
  }

  markDocumentTitleEdited(): void {
    this.documentTitleEdited = true;
  }

  /** Rewrites automatic section and document titles after the interface language changes. */
  private retitleForLocale(): void {
    for (const section of this.sections) {
      if (section.fromTemplate || this.titleEdited.has(section.datasetId)) {
        continue;
      }
      const dataset = this.sessionDatasets().find((item) => item.dataset_id === section.datasetId);
      if (!dataset) {
        continue;
      }
      section.title = this.autoDatasetLabel(dataset);
      this.sectionTitles.set(section.datasetId, section.title);
    }
    if (!this.documentTitleEdited && this.latestDataset) {
      this.documentTitle = this.autoDatasetLabel(this.latestDataset);
    }
  }

  addPendingSection(): void {
    if (!this.pendingDatasetId) {
      return;
    }
    this.addSection(this.pendingDatasetId);
    this.pendingDatasetId = '';
  }

  private apiError(err: HttpErrorResponse, fallbackKey: string): string {
    const body = err?.error;
    const message = typeof body === 'object' && body ? body.error || body.message : null;
    return typeof message === 'string' && message.trim() ? message : this.constructorText(fallbackKey);
  }

  refreshSections(): void {
    if (!this.sections.length || this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.composeError = '';
    const body = {
      geo_id: this.filterGeoId,
      period_from: this.periodFrom || null,
      period_to: this.periodTo || null,
      crop_id: this.filterCropId
    };
    forkJoin(
      this.sections.map((section) =>
        this.catalog.refreshDataset(section.datasetId, body).pipe(
          map((result) => ({ section, result, error: null as string | null })),
          catchError((err: HttpErrorResponse) =>
            of({ section, result: null as RefreshDatasetResponse | null, error: this.apiError(err, 'constructor_filter_rejected') })
          )
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        this.refreshing = false;
        const errors: string[] = [];
        for (const item of results) {
          if (!item.result) {
            errors.push(`${item.section.title}: ${item.error ?? this.constructorText('constructor_filter_rejected')}`);
            continue;
          }
          this.applyRefresh(item.section, item.result);
        }
        if (errors.length) {
          this.composeError = errors.join(' · ');
        }
      });
  }

  applyColumnPreset(preset: 'short' | 'all'): void {
    const catalog = this.readColumnCatalog();
    this.selectedColumnKeys = preset === 'all' ? indicatorKeys(catalog) : defaultKeys(catalog);
    this.columnError = false;
  }

  isColumnSelected(key: string): boolean {
    return this.selectedColumnKeys.includes(key);
  }

  toggleColumn(key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedColumnKeys = checked
      ? this.selectedColumnKeys.includes(key) ? this.selectedColumnKeys : [...this.selectedColumnKeys, key]
      : this.selectedColumnKeys.filter((item) => item !== key);
    this.columnError = false;
  }

  onColumnDragStart(key: string, event: DragEvent): void {
    if (!this.constructorMode) {
      event.preventDefault();
      return;
    }
    this.draggingColumn = key;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', key);
    }
  }

  onColumnDragOver(key: string, event: DragEvent): void {
    if (!this.draggingColumn || this.draggingColumn === key) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dragOverColumn = key;
  }

  onColumnDrop(key: string, event: DragEvent): void {
    event.preventDefault();
    const from = this.draggingColumn;
    this.draggingColumn = null;
    this.dragOverColumn = null;
    if (from && from !== key) {
      this.moveColumn(from, key);
    }
  }

  onColumnDragEnd(): void {
    this.draggingColumn = null;
    this.draggingSectionId = null;
    this.dragOverColumn = null;
  }

  private moveColumn(from: string, to: string): void {
    const keys = [...this.selectedColumnKeys];
    const fromIndex = keys.indexOf(from);
    const toIndex = keys.indexOf(to);
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }
    keys.splice(fromIndex, 1);
    keys.splice(toIndex, 0, from);
    this.selectedColumnKeys = keys;
  }

  columnLabel(column: ReportColumnOption): string {
    return knownColumnLabel(column.key, this.locale)
      || (this.locale === 'uz' ? column.label_uz : this.locale === 'en' ? column.label_en : column.label_ru)
      || column.label_ru
      || column.label_en
      || column.key;
  }

  /** Row key that holds the value of a column; aliases map to the name column in the interface language. */
  private resolvePreviewKey(column: string, available: ReadonlySet<string>): string | null {
    const prefix = COLUMN_ALIASES[column];
    if (!prefix) {
      return available.has(column) ? column : null;
    }
    const order = [`${prefix}_${this.locale}`, `${prefix}_ru`, `${prefix}_uz`, `${prefix}_en`];
    return order.find((key) => available.has(key)) ?? null;
  }

  columnLabelForKey(key: string, catalog?: ReportColumnOption[]): string {
    const column = (catalog ?? this.readColumnCatalog()).find((item) => item.key === key);
    return column ? this.columnLabel(column) : key;
  }

  openSession(session: ChatSessionSummary): void {
    if (this.renamingSessionId === session.session_id) {
      return;
    }
    this.loadSession(session.session_id);
  }

  openSessionFromArchive(sessionId: string): void {
    this.activeModuleTab = 'chat';
    this.loadSession(sessionId);
    this.refreshSessions();
  }

  toggleStar(session: ChatSessionSummary, event: Event): void {
    event.stopPropagation();
    this.catalog.updateSession(session.session_id, { starred: !session.starred }).subscribe({
      next: (updated) => this.replaceSession(updated),
      error: () => this.refreshSessions()
    });
  }

  archiveSession(session: ChatSessionSummary, event: Event): void {
    event.stopPropagation();
    this.catalog.updateSession(session.session_id, { archived: true }).subscribe({
      next: () => {
        this.sessions = this.sessions.filter((s) => s.session_id !== session.session_id);
        if (this.sessionId === session.session_id) {
          this.newSession();
        }
      },
      error: () => this.refreshSessions()
    });
  }

  deleteSession(session: ChatSessionSummary, event: Event): void {
    event.stopPropagation();
    if (!confirm(this.translate.instant('reporting_analytics.confirm_delete_session'))) {
      return;
    }
    this.catalog.deleteSession(session.session_id).subscribe({
      next: () => {
        this.sessions = this.sessions.filter((s) => s.session_id !== session.session_id);
        if (this.sessionId === session.session_id) {
          this.newSession();
        }
      },
      error: () => this.refreshSessions()
    });
  }

  startRename(session: ChatSessionSummary, event: Event): void {
    event.stopPropagation();
    this.renamingSessionId = session.session_id;
    this.renameDraft = this.sessionTitle(session);
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector<HTMLInputElement>('.rename-input');
      input?.focus();
      input?.select();
    });
  }

  commitRename(session: ChatSessionSummary): void {
    const title = this.renameDraft.trim();
    this.renamingSessionId = null;
    if (!title || title === session.title) {
      return;
    }
    this.catalog.updateSession(session.session_id, { title }).subscribe({
      next: (updated) => this.replaceSession(updated),
      error: () => this.refreshSessions()
    });
  }

  cancelRename(): void {
    this.renamingSessionId = null;
  }

  sendPrompt(key: string): void {
    this.draft = this.translate.instant(key);
    this.send();
  }

  send(): void {
    const text = this.draft.trim();
    if (!text || this.sending) {
      return;
    }
    this.draft = '';
    this.messages.push({
      id: newAnalyticsId(),
      role: 'user',
      text,
      time: new Date()
    });
    this.sending = true;
    this.scrollToEnd();
    this.chatApi.sendMessage(text, this.sessionId, this.locale).subscribe({
      next: (res) => this.onReply(res),
      error: (err: HttpErrorResponse) => this.onError(err)
    });
  }

  exportFile(kind: string): void {
    if (!this.constructorMode) {
      this.exportStoredDataset(kind);
      return;
    }
    this.syncSections();
    this.composeError = '';
    if (!this.sections.length) {
      this.composeError = this.constructorText('constructor_no_dataset');
      return;
    }
    if (this.sections.some((section) => !section.selectedKeys.length)) {
      this.columnError = true;
      return;
    }
    this.columnError = false;
    const format = kind === 'excel' ? 'xlsx' : kind;
    this.queueCompose({
      title: this.documentTitle.trim() || null,
      period_from: this.periodFrom || null,
      period_to: this.periodTo || null,
      geo_id: this.filterGeoId || null,
      crop_id: this.filterCropId || null,
      format,
      locale: this.locale,
      sections: (format === 'csv' ? this.sections.slice(0, 1) : this.sections).map((section) => ({
        dataset_id: section.datasetId,
        title: section.title.trim() || section.tool,
        columns: section.selectedKeys
      }))
    });
  }

  /** Chat export builds the file from the stored dataset. The model is not asked to paste the rows. */
  private exportStoredDataset(kind: string): void {
    const dataset = this.latestDataset;
    if (!dataset?.dataset_id) {
      this.exportFollowUp(kind);
      return;
    }
    const columns = defaultKeys(this.catalogOf(dataset));
    const period = dataset.meta?.['period'];
    const from = period && typeof period === 'object' ? (period as { from?: unknown }).from : null;
    const to = period && typeof period === 'object' ? (period as { to?: unknown }).to : null;
    const geoId = dataset.meta?.['geo_id'];
    const cropId = dataset.meta?.['crop_id'];
    this.composeError = '';
    this.queueCompose({
      title: this.datasetLabel(dataset),
      period_from: typeof from === 'string' ? from.slice(0, 10) : null,
      period_to: typeof to === 'string' ? to.slice(0, 10) : null,
      geo_id: typeof geoId === 'string' && geoId ? geoId : null,
      crop_id: typeof cropId === 'string' && cropId ? cropId : null,
      format: kind === 'excel' ? 'xlsx' : kind,
      locale: this.locale,
      sections: [{
        dataset_id: dataset.dataset_id,
        title: this.datasetLabel(dataset),
        columns
      }]
    });
  }

  private queueCompose(body: Parameters<AnalyticsCatalogService['composeReport']>[0]): void {
    this.catalog.composeReport(body).subscribe({
      next: (job) => {
        this.extraJobs = [...this.extraJobs, {
          tool: 'compose',
          job_id: job.job_id,
          status: job.status,
          progress_pct: job.progress_pct,
          format: job.format,
          download_url: job.download_url,
          report_id: job.report_id,
          error: job.error,
          file_name: job.status === 'done' ? this.jobFileName(job.title, job.format) : null
        }];
        this.startJobPolling(this.collectActiveJobs());
      },
      error: (err: HttpErrorResponse) => {
        const bodyError = err.error;
        this.composeError = String(
          (typeof bodyError === 'object' && (bodyError?.error || bodyError?.message)) ||
          this.constructorText('constructor_compose_failed')
        );
      }
    });
  }

  addSection(datasetId: string): void {
    const dataset = this.sessionDatasets().find((item) => item.dataset_id === datasetId);
    if (!dataset?.dataset_id || this.sections.some((section) => section.datasetId === dataset.dataset_id)) {
      return;
    }
    this.sections = [...this.sections, this.sectionFrom(dataset)];
  }

  removeSection(datasetId: string): void {
    this.sections = this.sections.filter((section) => section.datasetId !== datasetId);
  }

  sectionColumns(section: ConstructorSection): string[] {
    const available = new Set(section.previewColumns);
    return section.selectedKeys.filter((key) => this.resolvePreviewKey(key, available) !== null);
  }

  sectionMissingSelection(section: ConstructorSection): boolean {
    const preview = new Set(section.previewColumns);
    return section.selectedKeys.some((key) => this.resolvePreviewKey(key, preview) === null);
  }

  applySectionPreset(section: ConstructorSection): void {
    // Replaces the indicators and keeps whichever ids the user already added.
    const ids = section.selectedKeys.filter((key) => this.isIdentifier(section, key));
    section.selectedKeys = [...indicatorKeys(section.catalog), ...ids];
    this.columnError = false;
  }

  /** Catalog entries shown as checkboxes: indicators always, ids only once they are selected. */
  visibleCatalog(section: ConstructorSection): ReportColumnOption[] {
    return section.catalog.filter((column) => !isIdentifierColumn(column) || section.selectedKeys.includes(column.key));
  }

  sectionIdentifiers(section: ConstructorSection): ReportColumnOption[] {
    return section.catalog.filter((column) => isIdentifierColumn(column));
  }

  sectionHasAllIdentifiers(section: ConstructorSection): boolean {
    const ids = this.sectionIdentifiers(section);
    return ids.length > 0 && ids.every((column) => section.selectedKeys.includes(column.key));
  }

  /** Adds every id column to the table, or removes them all when they are already there. */
  toggleSectionIdentifiers(section: ConstructorSection): void {
    const ids = this.sectionIdentifiers(section).map((column) => column.key);
    if (!ids.length) {
      return;
    }
    const idSet = new Set(ids);
    section.selectedKeys = this.sectionHasAllIdentifiers(section)
      ? section.selectedKeys.filter((key) => !idSet.has(key))
      : [...section.selectedKeys.filter((key) => !idSet.has(key)), ...ids];
    this.columnError = false;
  }

  private isIdentifier(section: ConstructorSection, key: string): boolean {
    const column = section.catalog.find((item) => item.key === key);
    return column ? isIdentifierColumn(column) : /_id$/i.test(key);
  }

  toggleSectionColumn(section: ConstructorSection, key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    section.selectedKeys = checked
      ? section.selectedKeys.includes(key) ? section.selectedKeys : [...section.selectedKeys, key]
      : section.selectedKeys.filter((item) => item !== key);
    this.columnError = false;
  }

  onSectionDragStart(section: ConstructorSection, key: string, event: DragEvent): void {
    this.draggingSectionId = section.datasetId;
    this.onColumnDragStart(key, event);
  }

  onSectionDrop(section: ConstructorSection, key: string, event: DragEvent): void {
    event.preventDefault();
    const from = this.draggingColumn;
    const sameSection = this.draggingSectionId === section.datasetId;
    this.draggingColumn = null;
    this.draggingSectionId = null;
    this.dragOverColumn = null;
    if (sameSection && from && from !== key) {
      this.moveSectionColumn(section, from, key);
    }
  }

  exportFollowUp(kind: string): void {
    const map: Record<string, string> = {
      csv: 'Экспортируй последний датасет в CSV',
      excel: 'Сформируй Excel по последнему датасету',
      xls: 'Сформируй XLS по последнему датасету',
      pdf: 'Сформируй официальный PDF по последнему датасету',
      html: 'Собери HTML-дашборд по последнему датасету',
      docx: 'Сформируй DOCX по последнему датасету',
      geojson: 'Экспортируй последний датасет в GeoJSON',
      report: 'Сформируй отчёт PDF по последнему датасету'
    };
    this.columnError = false;
    this.draft = map[kind] || map['report'];
    this.send();
  }

  retryJob(): void {
    this.draft = 'Повтори формирование отчёта по последнему заданию';
    this.send();
  }

  downloadArtifact(file: ChatFileArtifact): void {
    const request = file.download_url
      ? this.chatApi.downloadUrl(file.download_url, file.file_name)
      : file.report_id
        ? this.isMcpReportId(file.report_id)
          ? this.chatApi.downloadReport(file.report_id, file.file_name)
          : this.chatApi.downloadUrl(
              `/api/orchestrator/assistant-files/${encodeURIComponent(file.report_id)}`,
              file.file_name
            )
        : null;
    request?.subscribe({
      error: (err: HttpErrorResponse) => this.onDownloadError(err)
    });
  }

  isImageArtifact(file: ChatFileArtifact): boolean {
    return file.content_type?.toLowerCase().startsWith('image/') === true;
  }

  imagePreview(file: ChatFileArtifact): string | null {
    return this.imagePreviewUrls.get(this.fileArtifactKey(file)) ?? null;
  }

  downloadJob(job: ChatJobArtifact): void {
    if (job.report_id) {
      this.chatApi.downloadReport(job.report_id, job.file_name).subscribe();
      return;
    }
    if (job.download_url) {
      this.chatApi.downloadUrl(job.download_url, job.file_name).subscribe();
    }
  }

  toggleAudit(id: string): void {
    if (this.auditOpen.has(id)) {
      this.auditOpen.delete(id);
    } else {
      this.auditOpen.add(id);
    }
  }

  private sessionDatasets(): ChatDatasetArtifact[] {
    return this.messages.flatMap((message) => message.artifacts?.datasets ?? []);
  }

  private syncSections(): void {
    const datasets = this.sessionDatasets().filter((dataset) => dataset.dataset_id);
    const known = new Map(datasets.map((dataset) => [dataset.dataset_id as string, dataset]));
    // Template sections live only on the server, so they are kept until the user removes them.
    this.sections = this.sections.filter((section) => section.fromTemplate || known.has(section.datasetId));
    if (this.sections.length || !datasets.length) {
      return;
    }
    const latest = datasets[datasets.length - 1];
    this.sections = [this.sectionFrom(latest)];
    this.fillDocumentFrom(latest);
  }

  private sectionFrom(dataset: ChatDatasetArtifact): ConstructorSection {
    const catalog = this.catalogOf(dataset);
    return {
      datasetId: dataset.dataset_id || '',
      tool: dataset.tool,
      title: this.datasetLabel(dataset),
      catalog,
      selectedKeys: defaultKeys(catalog),
      previewRows: dataset.preview_rows ?? [],
      previewColumns: dataset.columns ?? []
    };
  }

  private catalogOf(dataset: ChatDatasetArtifact): ReportColumnOption[] {
    const raw = dataset.meta?.['available_columns'];
    if (Array.isArray(raw)) {
      return raw.flatMap((item) => {
        if (!item || typeof item !== 'object') {
          return [];
        }
        const column = item as ReportColumnOption;
        return typeof column.key === 'string' && column.key.length > 0 ? [column] : [];
      });
    }
    return (dataset.columns ?? []).map((key) => ({ key, default_selected: true }));
  }

  private fillDocumentFrom(dataset: ChatDatasetArtifact): void {
    const period = dataset.meta?.['period'];
    if (!this.documentTitle) {
      this.documentTitle = this.datasetLabel(dataset);
    }
    if (period && typeof period === 'object') {
      const value = period as { from?: unknown; to?: unknown };
      if (!this.periodFrom && typeof value.from === 'string') {
        this.periodFrom = value.from.slice(0, 10);
      }
      if (!this.periodTo && typeof value.to === 'string') {
        this.periodTo = value.to.slice(0, 10);
      }
    }
    const geoId = dataset.meta?.['geo_id'];
    if (!this.filterGeoId && typeof geoId === 'string' && geoId.length > 0) {
      this.filterGeoId = geoId;
      this.ensureRegionOption(geoId);
    }
    const cropId = dataset.meta?.['crop_id'];
    if (!this.filterCropId && typeof cropId === 'string' && cropId.length > 0) {
      this.filterCropId = cropId;
    }
  }

  private loadTemplates(): void {
    this.catalog
      .listConstructorTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.savedTemplates = items;
        },
        error: () => undefined
      });
  }

  private applyTemplate(result: ApplyConstructorTemplateResponse): void {
    this.documentTitle = result.title;
    this.documentTitleEdited = true;
    this.templateName = result.title;
    this.periodFrom = (result.period_from || '').slice(0, 10);
    this.periodTo = (result.period_to || '').slice(0, 10);
    this.filterGeoId = result.geo_id || '';
    this.filterCropId = result.crop_id || '';
    if (this.filterGeoId) {
      this.ensureRegionOption(this.filterGeoId);
    }
    this.sections = (result.sections ?? []).map((section) => {
      const previewRows = section.preview_rows ?? [];
      const catalog = Array.isArray(section.columns)
        ? section.columns.filter((column) => typeof column.key === 'string' && column.key.length > 0)
        : [];
      const catalogKeys = new Set(catalog.map((column) => column.key));
      const selected = (section.selected_columns ?? []).filter((key) => !catalog.length || catalogKeys.has(key));
      const previewColumns = previewRows.length ? Object.keys(previewRows[0]) : indicatorKeys(catalog);
      return {
        datasetId: section.dataset_id,
        tool: section.tool,
        title: section.title || section.tool,
        catalog,
        selectedKeys: selected.length ? selected : defaultKeys(catalog),
        previewRows,
        previewColumns,
        fromTemplate: true
      };
    });
  }

  private loadFilterCatalogs(): void {
    if (this.filterCatalogsLoaded) {
      return;
    }
    this.filterCatalogsLoaded = true;
    this.chatApi
      .callTool<{ items?: Array<Record<string, unknown>> }>('list_geo')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.regionOptions = (data?.items ?? []).flatMap((item) => this.filterOption(item, 'geo_id'));
        if (this.filterGeoId) {
          this.ensureRegionOption(this.filterGeoId);
        }
      });
    this.chatApi
      .callTool<{ crops?: Array<Record<string, unknown>> }>('list_crops', { monitoring: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.cropOptions = (data?.crops ?? [])
          .filter((item) => item['status'] === 1 && item['is_monitoring'] === true)
          .flatMap((item) => this.filterOption(item, 'crop_id'));
        if (this.filterCropId && !this.cropOptions.some((option) => option.id === this.filterCropId)) {
          if (this.filterCropId === this.deepLinkCropId) {
            this.addDeepLinkCropOption(this.filterCropId);
          } else {
            this.filterCropId = '';
          }
        }
      });
  }

  private filterOption(item: Record<string, unknown>, idKey: string): FilterOption[] {
    const id = item[idKey];
    if (typeof id !== 'string' || !id) {
      return [];
    }
    const label =
      (this.locale === 'uz' ? item['name_uz'] : this.locale === 'en' ? item['name_en'] : item['name_ru']) ||
      item['name_ru'] ||
      item['name_en'] ||
      item['name_uz'] ||
      id;
    return [{ id, label: String(label) }];
  }

  /** Keeps a crop chosen in the GIS summary selectable, named from the crop dictionary when possible. */
  private addDeepLinkCropOption(cropId: string): void {
    this.cropOptions = [...this.cropOptions, { id: cropId, label: cropId }];
    this.cropCache
      .getCrops()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((crops) => {
        const crop = crops?.find((item) => item.id === cropId);
        if (crop) {
          const label = this.serverTranslate.getDisplayValue(crop, 'names');
          this.cropOptions = this.cropOptions.map((option) => (option.id === cropId ? { ...option, label } : option));
        }
      });
  }

  private ensureRegionOption(geoId: string): void {
    if (!this.regionOptions.some((option) => option.id === geoId)) {
      this.regionOptions = [...this.regionOptions, { id: geoId, label: geoId }];
    }
  }

  private applyRefresh(section: ConstructorSection, result: RefreshDatasetResponse): void {
    const previewRows = result.preview_rows ?? [];
    const catalog = Array.isArray(result.columns) && result.columns.length
      ? result.columns.filter((column) => typeof column.key === 'string' && column.key.length > 0)
      : section.catalog;
    const catalogKeys = new Set(catalog.map((column) => column.key));
    let selectedKeys = section.selectedKeys.filter((key) => catalogKeys.has(key));
    if (!selectedKeys.length) {
      selectedKeys = defaultKeys(catalog);
    }
    const previewColumns = previewRows.length
      ? Object.keys(previewRows[0])
      : indicatorKeys(catalog);
    section.tool = result.tool || section.tool;
    section.catalog = catalog;
    section.selectedKeys = selectedKeys;
    section.previewRows = previewRows;
    section.previewColumns = previewColumns;
    this.patchStoredDataset(section.datasetId, result, previewColumns);
  }

  private patchStoredDataset(datasetId: string, result: RefreshDatasetResponse, previewColumns: string[]): void {
    for (const message of this.messages) {
      const dataset = message.artifacts?.datasets?.find((item) => item.dataset_id === datasetId);
      if (!dataset) {
        continue;
      }
      dataset.tool = result.tool || dataset.tool;
      dataset.meta = result.meta ?? dataset.meta;
      dataset.preview_rows = result.preview_rows ?? [];
      dataset.columns = previewColumns;
    }
  }

  private moveSectionColumn(section: ConstructorSection, from: string, to: string): void {
    const keys = [...section.selectedKeys];
    const fromIndex = keys.indexOf(from);
    const toIndex = keys.indexOf(to);
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }
    keys.splice(fromIndex, 1);
    keys.splice(toIndex, 0, from);
    section.selectedKeys = keys;
  }

  private readColumnCatalog(): ReportColumnOption[] {
    const raw = this.latestDataset?.meta?.['available_columns'];
    if (Array.isArray(raw)) {
      return raw.flatMap((item) => {
        if (!item || typeof item !== 'object') {
          return [];
        }
        const column = item as ReportColumnOption;
        return typeof column.key === 'string' && column.key.length > 0 ? [column] : [];
      });
    }
    return (this.latestDataset?.columns ?? []).map((key) => ({ key, default_selected: true }));
  }

  private ensureColumnSelection(): void {
    const dataset = this.latestDataset;
    if (!dataset) {
      this.columnDatasetId = null;
      this.selectedColumnKeys = [];
      return;
    }
    const catalog = this.readColumnCatalog();
    const id = `${dataset.tool}|${String(dataset.meta?.['row_count'] ?? '')}|${catalog.map((column) => column.key).join(',')}`;
    if (id === this.columnDatasetId) {
      return;
    }
    this.columnDatasetId = id;
    this.selectedColumnKeys = defaultKeys(catalog);
  }

  errorText(code: string | null | undefined, ru?: string | null, uz?: string | null, fallback?: string | null): string {
    if (this.locale === 'uz' && uz) {
      return uz;
    }
    return ru || fallback || code || '';
  }

  private loadSession(sessionId: string): void {
    this.clearImagePreviews();
    this.sessionId = sessionId;
    this.sessionsStore.setCurrent(this.userId, sessionId);
    this.messages = [];
    this.composeError = '';
    this.composeNotice = '';
    this.documentTitle = '';
    this.periodFrom = '';
    this.periodTo = '';
    this.filterGeoId = '';
    this.filterCropId = '';
    this.templateName = '';
    this.selectedTemplateId = '';
    this.pendingDatasetId = '';
    this.sectionTitles.clear();
    this.titleEdited.clear();
    this.documentTitleEdited = false;
    this.sections = [];
    this.extraJobs = [];
    this.llmUnavailable = false;
    this.loadingHistory = true;
    this.pollSub?.unsubscribe();
    this.catalog.getSession(sessionId).subscribe({
      next: (details) => {
        this.loadingHistory = false;
        if (this.sessionId !== sessionId) {
          return;
        }
        this.messages = details.messages.map((m) => this.toChatMessage(m));
        if (this.constructorMode) {
          this.syncSections();
        }
        this.messages.forEach((message) => this.loadImagePreviews(message.artifacts));
        this.startJobPolling(this.collectActiveJobs());
        this.loadSessionJobs(sessionId);
        this.scrollToEnd();
      },
      error: (err: HttpErrorResponse) => {
        this.loadingHistory = false;
        if (err.status === 404) {
          // The session was deleted elsewhere (or belongs to another user): start clean.
          this.newSession();
          this.refreshSessions();
        }
      }
    });
  }

  private toChatMessage(record: ChatMessageRecord): AnalyticsChatMessage {
    const isAssistant = record.role === 'assistant';
    return {
      id: `srv-${record.id}`,
      role: isAssistant ? 'assistant' : 'user',
      text: record.text || (record.error ?? ''),
      time: new Date(record.created_at_utc),
      artifacts: isAssistant ? record.artifacts ?? emptyArtifacts() : undefined,
      tool_calls: record.tool_calls ?? undefined,
      correlation_id: record.correlation_id ?? undefined,
      error: record.error
    };
  }

  private onReply(res: ChatHttpResponse): void {
    this.sending = false;
    this.llmUnavailable = false;
    this.sessionId = res.session_id;
    this.sessionsStore.setCurrent(this.userId, res.session_id);
    this.sessionContext = res.session_context ?? this.sessionContext;
    this.messages.push({
      id: newAnalyticsId(),
      role: 'assistant',
      text: res.reply,
      time: new Date(),
      artifacts: res.artifacts ?? emptyArtifacts(),
      tool_calls: res.tool_calls,
      rounds: res.rounds,
      session_id: res.session_id,
      correlation_id: res.correlation_id,
      error: res.error
    });
    this.loadImagePreviews(res.artifacts);
    if (this.constructorMode) {
      this.syncSections();
    }
    // An answer that came back with contours or points is about a place: open the map for it.
    if (!this.constructorMode && ReportingAnalyticsPageComponent.turnHasGeography(res.artifacts)) {
      this.setResultTab('map');
    }
    this.refreshSessions();
    this.startJobPolling(res.artifacts?.jobs ?? []);
    this.scrollToEnd();
  }

  private loadImagePreviews(artifacts?: ChatTurnArtifacts): void {
    for (const file of artifacts?.files ?? []) {
      if (!this.isImageArtifact(file) || !file.download_url) {
        continue;
      }
      const key = this.fileArtifactKey(file);
      if (this.imagePreviewUrls.has(key)) {
        continue;
      }
      this.chatApi.loadFilePreview(file.download_url)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (url) => this.imagePreviewUrls.set(key, url)
        });
    }
  }

  private clearImagePreviews(): void {
    this.imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.imagePreviewUrls.clear();
  }

  private fileArtifactKey(file: ChatFileArtifact): string {
    return file.report_id || file.download_url || file.file_name || '';
  }

  private isMcpReportId(reportId: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(reportId);
  }

  private onDownloadError(err: HttpErrorResponse): void {
    const body = err.error;
    const message =
      (typeof body === 'object' && (body?.error || body?.message)) ||
      err.message ||
      'File download failed';
    this.messages.push({
      id: newAnalyticsId(),
      role: 'assistant',
      text: String(message),
      time: new Date(),
      artifacts: emptyArtifacts(),
      error: String(message)
    });
    this.scrollToEnd();
  }

  private onError(err: HttpErrorResponse): void {
    this.sending = false;
    if (err.status === 503) {
      this.llmUnavailable = true;
      return;
    }
    if (err.status === 403) {
      // session_id belongs to another user: drop it and let the next message open a fresh session.
      this.sessionId = null;
      this.sessionsStore.clearCurrent(this.userId);
    }
    const body = err.error;
    const message =
      (typeof body === 'object' && (body?.error || body?.message)) ||
      err.message ||
      this.translate.instant('reporting_analytics.request_failed');
    this.messages.push({
      id: newAnalyticsId(),
      role: 'assistant',
      text: String(message),
      time: new Date(),
      artifacts: emptyArtifacts(),
      error: String(message)
    });
  }

  private startJobPolling(jobs: ChatJobArtifact[]): void {
    const active = jobs.filter((j) => j.job_id && isActiveJobStatus(j.status));
    this.pollSub?.unsubscribe();
    if (!active.length) {
      return;
    }
    this.pollSub = interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const pending = this.collectActiveJobs();
        if (!pending.length) {
          this.pollSub?.unsubscribe();
          return;
        }
        pending.forEach((job) => {
          this.catalog.getJob(job.job_id).subscribe({
            next: (updated) => this.applyJobUpdate(updated),
            error: () => undefined
          });
        });
      });
  }

  /**
   * Constructor files are queued outside the chat turn, so they are not in message artifacts.
   * Reload them from the job catalog so the queue survives a page refresh.
   */
  private loadSessionJobs(sessionId: string): void {
    this.catalog
      .listJobs({ session_id: sessionId, take: 50 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (jobs) => {
          if (this.sessionId !== sessionId) {
            return;
          }
          const known = new Set(this.allJobs.map((job) => job.job_id));
          const restored = jobs
            .filter((job) => !known.has(job.job_id))
            .reverse()
            .map((job): ChatJobArtifact => ({
              tool: 'compose',
              job_id: job.job_id,
              status: job.status,
              progress_pct: job.progress_pct,
              format: job.format,
              download_url: job.download_url,
              report_id: job.report_id,
              error: job.error,
              file_name: job.status === 'done' ? this.jobFileName(job.title, job.format) : null
            }));
          if (!restored.length) {
            return;
          }
          this.extraJobs = [...this.extraJobs, ...restored];
          this.startJobPolling(this.collectActiveJobs());
        },
        error: () => undefined
      });
  }

  private applyJobUpdate(updated: ReportJobItem): void {
    this.extraJobs = this.extraJobs.map((job) =>
      job.job_id === updated.job_id
        ? {
            ...job,
            status: updated.status,
            progress_pct: updated.progress_pct,
            download_url: updated.download_url ?? job.download_url,
            report_id: updated.report_id ?? job.report_id,
            error: updated.error ?? null,
            file_name: job.file_name || (updated.status === 'done' ? this.jobFileName(updated.title, updated.format) : null)
          }
        : job
    );
    this.messages = this.messages.map((m) => {
      if (!m.artifacts?.jobs?.some((j) => j.job_id === updated.job_id)) {
        return m;
      }
      return {
        ...m,
        artifacts: {
          ...m.artifacts,
          jobs: m.artifacts.jobs.map((j) =>
            j.job_id === updated.job_id
              ? {
                  ...j,
                  status: updated.status,
                  progress_pct: updated.progress_pct,
                  download_url: updated.download_url ?? j.download_url,
                  report_id: updated.report_id ?? j.report_id,
                  file_name: j.file_name || (updated.status === 'done' ? this.jobFileName(updated.title, updated.format) : null),
                  error: updated.error ?? null
                }
              : j
          )
        }
      };
    });
  }

  private jobFileName(title?: string | null, format?: string | null): string {
    const ext = (format || 'bin').replace(/^\./, '').toLowerCase();
    const base = (title || 'report').trim() || 'report';
    return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
  }

  private collectActiveJobs(): ChatJobArtifact[] {
    return [...this.messages.flatMap((m) => m.artifacts?.jobs ?? []), ...this.extraJobs]
      .filter((j) => j.job_id && isActiveJobStatus(j.status));
  }

  private refreshSessions(): void {
    this.sessionsLoading = true;
    this.catalog
      .listSessions({ search: this.search, archived: 'false', take: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.sessions = items;
          this.sessionsLoading = false;
          this.sessionsError = false;
        },
        error: () => {
          this.sessionsLoading = false;
          this.sessionsError = true;
        }
      });
  }

  private replaceSession(updated: ChatSessionSummary): void {
    this.sessions = this.sessions.map((s) => (s.session_id === updated.session_id ? updated : s));
  }

  private scrollToEnd(): void {
    queueMicrotask(() => {
      const el = this.feed?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
